/**
 * Visit Detail Drawer (Phase 11)
 *
 * ESM replacement for the three inline ``<script>`` blocks that used
 * to live in ``templates/office/visits/partials/_detail_drawer.html``:
 *
 *   1. **Photo lightbox** (~45 LOC): open / close / prev / next +
 *      keyboard nav. The same overlay markup is reused for every
 *      photo set; we read the photo list from a per-occurrence
 *      ``json_script`` block.
 *
 *   2. **Assignment quick-fix AJAX form** (~85 LOC): POSTs the cleaner
 *      picker selection to ``office:update_assignment`` then **reloads
 *      the same drawer** (not the close-and-toast default), so the
 *      user sees the new assignment immediately. Reuses
 *      ``window.wireDrawerActionForm`` with a custom ``onSuccess``.
 *
 *   3. **Breaks management** (~95 LOC): Add Break (toggle, mutex
 *      between ``duration`` and ``start/end`` time inputs, submit) +
 *      Delete Break (confirm modal, AJAX, replace
 *      ``#breaks-section-N`` outerHTML). The mutex/submit/delete
 *      handlers are bound at the **document level** (gated by a
 *      module-scoped boolean) because the partial
 *      ``templates/office/visits/partials/_breaks_list.html`` is
 *      swapped wholesale by ``submitBreakForm`` / ``deleteBreak``
 *      themselves — a per-element bind would be lost on the first
 *      add/delete. The lightbox + assignment form, by contrast, are
 *      per-element since their markup never gets re-injected without
 *      a full drawer reload.
 *
 * Self-registers on ``window.visitDrawerInits.visitDetail`` so the
 * shell's ``wireBespokeDrawersInContent`` walker calls ``init(rootEl)``
 * after each partial load (see ``pages/visit-drawer.js``).
 *
 * Markup contract:
 *
 *   <div data-bespoke-init="visitDetail" data-occurrence-id="42">
 *     <script id="visit-detail-config-42" type="application/json">{...}</script>
 *     ... drawer markup with [data-detail-action="open-lightbox|..."]
 *         and [data-breaks-action="toggle|delete"] markers ...
 *   </div>
 *
 * The config JSON shape is:
 *   {
 *     "photos":   [{"url": "...", "type": "before|after|general", "time": "HH:MM"}, ...],
 *     "i18n":     {
 *       "before": "...", "after": "...",
 *       "assignmentUpdated": "...", "assignmentFailed": "...", "assignmentError": "...",
 *       "confirmDeleteBreak": "..."
 *     },
 *     "urls":     {"breakAddBase": "/office/visits/{occId}/breaks/add/"}
 *   }
 */

let breaksDelegateBound = false;
let drawerNavDelegateBound = false;

function readDetailConfig(rootEl) {
    const occId = rootEl.dataset.occurrenceId || '';
    const node = document.getElementById(`visit-detail-config-${occId}`);
    if (!node) return { photos: [], i18n: {}, urls: {} };
    try {
        return JSON.parse(node.textContent || '{}');
    } catch (err) {
        console.warn('visit-detail-drawer: invalid config JSON', err);
        return { photos: [], i18n: {}, urls: {} };
    }
}

// ─────────────────────────── Lightbox ────────────────────────────

function bindLightbox(rootEl, photos, i18n) {
    const lightbox = rootEl.querySelector('#photoLightbox');
    if (!lightbox || lightbox._lightboxBound) return;
    lightbox._lightboxBound = true;

    const img = lightbox.querySelector('#lightboxImage');
    const counter = lightbox.querySelector('#lightboxCounter');
    const caption = lightbox.querySelector('#lightboxCaption');
    let idx = 0;

    function update() {
        const p = photos[idx];
        if (!p || !img) return;
        img.src = p.url;
        if (counter) counter.textContent = `${idx + 1} / ${photos.length}`;
        if (caption) {
            const parts = [];
            if (p.type === 'before' && i18n.before) parts.push(i18n.before);
            else if (p.type === 'after' && i18n.after) parts.push(i18n.after);
            if (p.time) parts.push(p.time);
            if (parts.length) {
                caption.textContent = parts.join(' · ');
                caption.classList.remove('hidden');
            } else {
                caption.classList.add('hidden');
            }
        }
    }

    function open(i) {
        idx = i;
        update();
        lightbox.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
    }

    function close() {
        lightbox.classList.add('hidden');
        document.body.style.overflow = '';
    }

    function nav(delta) {
        if (!photos.length) return;
        idx = (idx + delta + photos.length) % photos.length;
        update();
    }

    // Backdrop click → close (only if the click is on the backdrop
    // itself, not on a child like the image or a nav button).
    lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) close();
    });

    // Delegated click for [data-detail-action] inside the lightbox
    // (close, prev, next).
    lightbox.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-detail-action]');
        if (!btn || !lightbox.contains(btn)) return;
        const action = btn.dataset.detailAction;
        if (action === 'close-lightbox') close();
        else if (action === 'prev-lightbox') nav(-1);
        else if (action === 'next-lightbox') nav(1);
    });

    // Delegated click for thumbnails [data-detail-action="open-lightbox"].
    rootEl.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-detail-action="open-lightbox"]');
        if (!thumb) return;
        const i = parseInt(thumb.dataset.photoIndex, 10);
        if (Number.isFinite(i)) open(i);
    });

    // Keyboard nav (Escape / ArrowLeft / ArrowRight) — single
    // document-level handler; self-detaches when the lightbox node
    // is removed from the DOM (drawer closed → content re-rendered).
    function onKeydown(e) {
        if (!document.body.contains(lightbox)) {
            document.removeEventListener('keydown', onKeydown);
            return;
        }
        if (lightbox.classList.contains('hidden')) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') nav(-1);
        else if (e.key === 'ArrowRight') nav(1);
    }
    document.addEventListener('keydown', onKeydown);
}

// ─────────────────────── Multi-cleaner tabs ──────────────────────

const TEAM_TAB_ACTIVE = [
    'bg-blue-100', 'text-blue-900', 'border-blue-300',
    'dark:bg-blue-900/50', 'dark:text-blue-200', 'dark:border-blue-600',
];
const TEAM_TAB_INACTIVE = [
    'bg-white', 'text-blue-700', 'border-blue-200', 'hover:bg-blue-50',
    'dark:bg-gray-800', 'dark:text-blue-300', 'dark:border-blue-700',
];
const TEAM_TABS_HINT_KEY = 'ctTeamTabsHintSeen';

/**
 * Badges-as-tabs for multi-cleaner visits: clicking a [data-team-tab="ID"]
 * badge shows the matching [data-team-pane="ID"] block (per-cleaner Time
 * Tracking / Notes / Photos / Breaks) and hides the others. Single-cleaner
 * drawers have no [data-team-tabs] wrapper, so this is a no-op for them.
 *
 * Per-element bind (flag on the wrapper node): the drawer markup is
 * re-injected on every reload, so a fresh wrapper always needs re-wiring —
 * a module-level flag would skip the rebind after a boosted nav (cf.
 * .cursor/rules/hx-boost-init.mdc rule 4).
 */
function bindTeamTabs(rootEl) {
    const tabsEl = rootEl.querySelector('[data-team-tabs]');
    if (!tabsEl || tabsEl._teamTabsBound) return;
    tabsEl._teamTabsBound = true;

    const setActive = (cleanerId) => {
        tabsEl.querySelectorAll('[data-team-tab]').forEach((tab) => {
            const isActive = tab.dataset.teamTab === cleanerId;
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.classList.remove(...(isActive ? TEAM_TAB_INACTIVE : TEAM_TAB_ACTIVE));
            tab.classList.add(...(isActive ? TEAM_TAB_ACTIVE : TEAM_TAB_INACTIVE));
        });
        rootEl.querySelectorAll('[data-team-pane]').forEach((pane) => {
            pane.classList.toggle('hidden', pane.dataset.teamPane !== cleanerId);
        });
    };

    // First-open affordance: show the helper line once per browser, then
    // dismiss it the first time the user actually switches tabs.
    const hint = tabsEl.querySelector('[data-team-hint]');
    let hintSeen = true;
    try {
        hintSeen = window.localStorage.getItem(TEAM_TABS_HINT_KEY) === '1';
    } catch {
        hintSeen = true;
    }
    if (hint && !hintSeen) hint.classList.remove('hidden');

    tabsEl.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-team-tab]');
        if (!tab || !tabsEl.contains(tab)) return;
        setActive(tab.dataset.teamTab);
        if (hint && !hint.classList.contains('hidden')) {
            hint.classList.add('hidden');
            try {
                window.localStorage.setItem(TEAM_TABS_HINT_KEY, '1');
            } catch { /* private mode: just skip persistence */ }
        }
    });
}

// ──────────────────────── Assignment Form ────────────────────────

function bindAssignmentForm(rootEl, i18n) {
    const form = rootEl.querySelector('#drawerAssignmentForm');
    if (!form || form._assignmentBound) return;
    form._assignmentBound = true;

    if (typeof window.wireDrawerActionForm !== 'function') {
        console.warn('visit-detail-drawer: wireDrawerActionForm missing');
        return;
    }

    const occId = rootEl.dataset.occurrenceId || '';

    window.wireDrawerActionForm({
        formId: 'drawerAssignmentForm',
        submitBtnId: 'drawerAssignmentSubmit',
        successMessage: i18n.assignmentUpdated || '',
        errorMessage: i18n.assignmentFailed || '',
        errorContainerId: 'drawerAssignmentError',
        // The default success path closes the drawer + soft-reloads the
        // /visits/ list — but the quick-fix picker is meant to *stay*
        // open so the user sees the new assignment. Custom onSuccess
        // refreshes the schedule views, fires the toast and re-opens
        // the same drawer to pull the post-save state.
        onSuccess: (payload) => {
            if (window.scheduleCalendar) {
                window.scheduleCalendar.refetchEvents();
            }
            if (window.ScheduleHub?.refresh?.team) {
                window.ScheduleHub.refresh.team();
            }
            const msg = (payload && payload.message) || i18n.assignmentUpdated || '';
            if (msg && typeof window.showToast === 'function') {
                window.showToast(msg, 'success');
            }
            if (occId && typeof window.showVisitDrawer === 'function') {
                window.showVisitDrawer(occId);
            }
        },
        // Custom onError: the legacy script also cleared the picker's
        // red border on retry; keep that and fall back to the inline
        // error container (no toast — the inline message is enough).
        onError: (error, helpers) => {
            const msg = error.message || i18n.assignmentError || '';
            helpers.showFormError(msg);
        },
    });
}

// ──────────────────────── Breaks Management ──────────────────────

/**
 * Toggle the inline "Add Break" form visibility for an occurrence.
 * The toggle button (Add Break) and the cancel button inside the
 * form both carry ``data-breaks-action="toggle"``.
 */
function toggleAddBreakForm(occId) {
    const form = document.getElementById(`addBreakForm-${occId}`);
    const btn = document.getElementById(`addBreakToggle-${occId}`);
    if (form) form.classList.toggle('hidden');
    if (btn) btn.classList.toggle('hidden');
}

/**
 * Mutex between the ``duration`` input and the ``start/end`` time
 * inputs of the Add Break form: setting a value in one column
 * disables the other(s). Driven by [data-breaks-mutex-role] markers
 * on the three inputs (``duration``, ``start``, ``end``) sharing a
 * common parent flagged with [data-breaks-mutex-group].
 */
function applyBreakMutex(group) {
    const dur = group.querySelector('[data-breaks-mutex-role="duration"]');
    const start = group.querySelector('[data-breaks-mutex-role="start"]');
    const end = group.querySelector('[data-breaks-mutex-role="end"]');
    if (!dur || !start || !end) return;
    if (dur.value) {
        start.disabled = true; end.disabled = true;
        start.value = ''; end.value = '';
        start.classList.add('opacity-50'); end.classList.add('opacity-50');
    } else if (start.value || end.value) {
        dur.disabled = true; dur.value = '';
        dur.classList.add('opacity-50');
        start.disabled = false; end.disabled = false;
        start.classList.remove('opacity-50'); end.classList.remove('opacity-50');
    } else {
        dur.disabled = false; start.disabled = false; end.disabled = false;
        dur.classList.remove('opacity-50');
        start.classList.remove('opacity-50'); end.classList.remove('opacity-50');
    }
}

/**
 * Submit the Add Break form via fetch and replace the section's
 * outerHTML with the partial returned by ``office:break_add``.
 */
function submitBreakForm(form) {
    const occId = form.dataset.occurrenceId;
    const url = form.action || form.dataset.actionUrl || '';
    if (!occId || !url) return;

    const errorEl = document.getElementById(`breakFormError-${occId}`);
    if (errorEl) {
        errorEl.classList.add('hidden');
        errorEl.textContent = '';
    }

    const formData = new FormData(form);

    fetch(url, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
        .then((response) => {
            if (response.ok) return response.text();
            return response.json().then((data) => {
                throw new Error(data.error || 'Error adding break');
            });
        })
        .then((html) => {
            const section = document.getElementById(`breaks-section-${occId}`);
            if (section) section.outerHTML = html;
            const fresh = document.getElementById(`breaks-section-${occId}`);
            if (fresh && typeof window.initCustomSelects === 'function') {
                window.initCustomSelects(fresh);
            }
            if (typeof window.reinitFlowbiteWidgets === 'function') {
                window.reinitFlowbiteWidgets();
            }
            // Reload the drawer to pull updated totals (work duration,
            // breaks total, paid total) from the server — same as the
            // legacy script.
            if (typeof window.showVisitDrawer === 'function') {
                window.showVisitDrawer(occId);
            }
        })
        .catch((err) => {
            if (errorEl) {
                errorEl.textContent = err.message;
                errorEl.classList.remove('hidden');
            }
        });
}

/**
 * Delete a break (confirm via showConfirmModal then AJAX). Replaces
 * the section's outerHTML with the partial returned by
 * ``office:break_delete``.
 */
function deleteBreak(occId, breakId, confirmMessage) {
    const exec = () => {
        const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;
        const formData = new FormData();
        if (csrfToken) formData.append('csrfmiddlewaretoken', csrfToken);

        fetch(`/office/visits/${occId}/breaks/${breakId}/delete/`, {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
            .then((response) => {
                if (response.ok) return response.text();
                return response.json().then((data) => {
                    throw new Error(data.error || 'Error deleting break');
                });
            })
            .then((html) => {
                const section = document.getElementById(`breaks-section-${occId}`);
                if (section) section.outerHTML = html;
                const fresh = document.getElementById(`breaks-section-${occId}`);
                if (fresh && typeof window.initCustomSelects === 'function') {
                    window.initCustomSelects(fresh);
                }
                if (typeof window.reinitFlowbiteWidgets === 'function') {
                    window.reinitFlowbiteWidgets();
                }
            })
            .catch((err) => {
                if (typeof window.showToast === 'function') {
                    window.showToast(err.message, 'error');
                } else {
                    alert(err.message);
                }
            });
    };

    if (typeof window.showConfirmModal === 'function') {
        window.showConfirmModal({
            message: confirmMessage,
            type: 'danger',
            onConfirm: exec,
        });
    } else if (window.confirm(confirmMessage)) {
        exec();
    }
}

/**
 * Bind the document-level breaks delegation exactly once per page
 * lifetime. Required because ``submitBreakForm`` / ``deleteBreak``
 * replace ``#breaks-section-N`` outerHTML — any per-element binding
 * would die on the first add/delete.
 */
function bindBreaksDelegationOnce() {
    if (breaksDelegateBound) return;
    breaksDelegateBound = true;

    document.body.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-breaks-action]');
        if (!btn) return;
        const action = btn.dataset.breaksAction;
        const occId = btn.dataset.occurrenceId;
        if (action === 'toggle' && occId) {
            toggleAddBreakForm(occId);
        } else if (action === 'delete' && occId && btn.dataset.breakId) {
            // The confirm message is shipped on the delete button via
            // ``data-confirm-message`` — picked from the json_script
            // i18n by the partial.
            const msg = btn.dataset.confirmMessage || '';
            deleteBreak(occId, btn.dataset.breakId, msg);
        }
    });

    document.body.addEventListener('submit', (event) => {
        const form = event.target.closest('form[data-breaks-action="submit"]');
        if (!form) return;
        event.preventDefault();
        submitBreakForm(form);
    });

    document.body.addEventListener('input', (event) => {
        const input = event.target.closest('[data-breaks-mutex-role]');
        if (!input) return;
        const group = input.closest('[data-breaks-mutex-group]');
        if (group) applyBreakMutex(group);
    });

    document.body.addEventListener('change', (event) => {
        const input = event.target.closest('[data-breaks-mutex-role]');
        if (!input) return;
        const group = input.closest('[data-breaks-mutex-group]');
        if (group) applyBreakMutex(group);
    });
}

// ───────────────────────── Init / Register ───────────────────────

// ───────────────────────── Drawer Navigation ─────────────────────

/**
 * Handle the footer "navigation" buttons that used to be inline
 * ``onclick="window.show*Drawer(...)"`` calls. The buttons carry
 * ``data-detail-action="show-drawer"`` + a ``data-drawer-target``.
 *
 * Bound at the document level (gated by ``drawerNavDelegateBound``)
 * because the footer ``<div class="absolute bottom-0">`` is a SIBLING
 * of the ``[data-bespoke-init="visitDetail"]`` wrapper in the partial,
 * not a child — a per-element bind on rootEl would never see footer
 * clicks. Document-level delegation also survives every drawer reload
 * for free (no rebind on swap, no idempotency book-keeping per node).
 *
 * PO 2026-05-22 — the ``show-job-drawer`` branch (used by the « View
 * Parent Job » link to chain the visit drawer → read-only job drawer)
 * has been removed alongside the unification of Job detail. That link
 * is now a real ``<a href>`` straight to ``office:job_detail`` (see
 * ``templates/office/visits/partials/_detail_drawer.html``), so the
 * browser handles the navigation natively (full-page load, cleaner
 * back-button behaviour, no JS state to sync).
 */
const DRAWER_TARGET_TO_GLOBAL = {
    reschedule: 'showRescheduleDrawer',
    'cleaner-absent': 'showCleanerAbsentDrawer',
    'no-show': 'showNoShowDrawer',
    'not-performed': 'showNotPerformedDrawer',
    cancel: 'showCancelDrawer',
    'mark-done': 'showMarkDoneDrawer',
};

const DRAWER_NAV_ACTIONS = new Set(['show-drawer']);

function bindDrawerNavigationOnce() {
    if (drawerNavDelegateBound) return;
    drawerNavDelegateBound = true;

    document.body.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-detail-action]');
        if (!btn) return;
        const action = btn.dataset.detailAction;
        // Lightbox actions (open/close/prev/next-lightbox) are bound
        // per-element by bindLightbox; bail out so we don't trigger
        // them twice or misroute them through the navigation handler.
        if (!DRAWER_NAV_ACTIONS.has(action)) return;

        if (action === 'show-drawer') {
            const target = btn.dataset.drawerTarget;
            const occId = btn.dataset.occurrenceId;
            const fnName = DRAWER_TARGET_TO_GLOBAL[target];
            if (!fnName || !occId) return;
            const fn = window[fnName];
            if (typeof fn === 'function') fn(occId);
        }
    });
}

function initVisitDetailDrawer(rootEl) {
    if (!rootEl) return;
    const config = readDetailConfig(rootEl);
    const photos = Array.isArray(config.photos) ? config.photos : [];
    const i18n = config.i18n || {};

    // Lightbox is per-element (markup re-rendered with each drawer load).
    if (photos.length) {
        bindLightbox(rootEl, photos, i18n);
    }

    // Assignment form is per-element too.
    bindAssignmentForm(rootEl, i18n);

    // Multi-cleaner badges-as-tabs (per-element; no-op for single-cleaner).
    bindTeamTabs(rootEl);

    // Footer / "Related" drawer navigation buttons (Reschedule,
    // Cleaner Absent, Cancel, Mark Done, View Parent Job, …).
    // Delegated at document level — the footer block is a SIBLING of
    // rootEl in the partial, not a descendant.
    bindDrawerNavigationOnce();

    // Breaks delegation must persist across ``_breaks_list.html``
    // outerHTML swaps, so it lives at document level.
    bindBreaksDelegationOnce();
}

if (typeof window !== 'undefined') {
    window.visitDrawerInits = window.visitDrawerInits || {};
    window.visitDrawerInits.visitDetail = initVisitDetailDrawer;
}

export { initVisitDetailDrawer };
